import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';

import { AuthService } from './auth.service';
import {
  PhiAccessAuditService,
  PhiResourceType,
  PhiAccessAction
} from './phi-access-audit.service';

/**
 * Route guard that logs PHI access when the user navigates to a PHI-related route.
 * Does not block navigation; runs after AuthGuard and records who viewed which resource.
 * Used for HIPAA audit trail coverage across all patient/client/study routes.
 */
@Injectable({
  providedIn: 'root'
})
export class PhiAccessAuditGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private phiAccessAudit: PhiAccessAuditService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    if (this.authService.userIsSignedIn.getValue()) {
      this.logFromRoute(route, state);
    }
    return true;
  }

  /**
   * Derive resource type and id from the activated route and log one PHI_ACCESS_AUDIT view.
   */
  private logFromRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): void {
    const path = route.routeConfig?.path ?? state.url.split('?')[0];
    const params = route.paramMap;

    let resourceType: PhiResourceType;
    let resourceId: string | null = null;

    if (path === 'profile/:uid' || path.includes('profile/')) {
      resourceType = 'patient';
      resourceId = params.get('uid');
    } else if (path.includes('clients/') && params.get('uid')) {
      resourceType = 'patient';
      resourceId = params.get('uid');
    } else if (
      path.includes('patient-share-documents') ||
      path === 'patient-share-documents/:patientID'
    ) {
      resourceType = 'patient';
      resourceId = params.get('patientID');
    } else if (
      path.includes('patient-retrieve-documents') ||
      path === 'patient-retrieve-documents/:patientID'
    ) {
      resourceType = 'patient';
      resourceId = params.get('patientID');
    } else if (path.includes('patient-profile') || path === 'patient-profile/:patientID') {
      resourceType = 'patient';
      resourceId = params.get('patientID');
    } else if (
      path.includes('patient-overview') ||
      path.includes('patient-overview-modal')
    ) {
      resourceType = 'patient';
      resourceId = params.get('caseName') ?? null;
    } else if (path.includes('image-exchange')) {
      resourceType = 'image-exchange';
      resourceId = params.get('patientId') ?? null;
    } else if (path === 'studies' || path === 'studies/test-integration') {
      resourceType = 'studies';
      resourceId = null;
    } else {
      return;
    }

    this.phiAccessAudit.logPhiAccess(resourceType, resourceId, 'view' as PhiAccessAction);
  }
}
